import { BookOpenTextIcon, CodeIcon, SparkleIcon } from '@phosphor-icons/react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { api, type Track } from './api.ts'
import { useAuth } from './AuthContext.tsx'
import { AuthModal } from './AuthModal.tsx'
import { NavBar } from './NavBar.tsx'
import { PaginatedTrackList } from './PaginatedTrackList.tsx'
import { Link, navigate } from './Router.tsx'
import { useNavBarHeightStore, usePublicTracksStore } from './store.ts'
import { TermsAndPrivacyModal } from './TermsAndPrivacyModal.tsx'
import { showToast, ToastContainer } from './Toast.tsx'
import { useTrackList } from './useTrackList.ts'

let hmrVersion = 0
let isHmrUpdating = false

if (import.meta.hot) {
  import.meta.hot.on('vite:beforeUpdate', () => {
    isHmrUpdating = true
    hmrVersion++
  })
  import.meta.hot.on('vite:afterUpdate', () => {
    isHmrUpdating = false
  })
}

export function PublicTracks() {
  const { user } = useAuth()
  const { tracks, cursor, hasMore, setTracks, appendTracks, setCursor, setHasMore, reset } = usePublicTracksStore()
  const [loading, setLoading] = useState(tracks.length === 0)
  const cursorRef = useRef<string | undefined>(cursor)
  const isLoadingRef = useRef(false)
  const hasInitializedRef = useRef(false)
  const lastHmrVersionRef = useRef(hmrVersion)
  const [needsHmrReload, setNeedsHmrReload] = useState(false)

  useEffect(() => {
    cursorRef.current = cursor
  }, [cursor])
  const [isVerySmallScreen, setIsVerySmallScreen] = useState(false)
  const [showAuthModal, setShowAuthModal] = useState(false)
  const [showAboutModal, setShowAboutModal] = useState(false)
  const [pendingLikeTrack, setPendingLikeTrack] = useState<Track | null>(null)
  const { height: navBarHeight } = useNavBarHeightStore()

  const trackList = useTrackList({
    pageType: 'publicTracks',
    tracks,
    loading,
    onTracksUpdate: setTracks,
  })

  const loadPublicTracks = useCallback(
    async (append = false) => {
      if (!append && isLoadingRef.current) {
        return
      }

      try {
        if (!append) {
          isLoadingRef.current = true
          setLoading(true)
          setCursor(undefined)
          cursorRef.current = undefined
        }

        const response = await api.tracks.getPublicTracks(
          5,
          append ? cursorRef.current : undefined,
        )

        if (append) {
          appendTracks(response.tracks)
        }
        else {
          setTracks(response.tracks)
        }

        setCursor(response.cursor)
        cursorRef.current = response.cursor
        setHasMore(!!response.cursor)

        trackList.loadTrackData(response.tracks, append)
      }
      catch (err: any) {
        console.error(err)
        showToast(err.message)
      }
      finally {
        if (!append) {
          isLoadingRef.current = false
          setLoading(false)
        }
      }
    },
    [setTracks, appendTracks, setCursor, setHasMore, trackList],
  )

  useEffect(() => {
    const isHmrUpdate = lastHmrVersionRef.current !== hmrVersion
    if (isHmrUpdate) {
      lastHmrVersionRef.current = hmrVersion
      hasInitializedRef.current = false
      if (tracks.length > 0) {
        setNeedsHmrReload(true)
      }
    }

    if (!hasInitializedRef.current) {
      hasInitializedRef.current = true
      if (tracks.length === 0) {
        setLoading(true)
        loadPublicTracks()
      }
      else {
        setLoading(false)
      }
    }
  }, [loadPublicTracks, tracks.length])

  useEffect(() => {
    if (needsHmrReload && tracks.length > 0) {
      setNeedsHmrReload(false)
      trackList.loadTrackData([...tracks], false)
    }
  }, [needsHmrReload, tracks.length, trackList])

  useEffect(() => {
    if (tracks.length > 0) {
      setLoading(false)
    }
  }, [tracks.length])

  useEffect(() => {
    const checkScreenSize = () => {
      setIsVerySmallScreen(window.innerWidth < 700)
    }

    checkScreenSize()
    window.addEventListener('resize', checkScreenSize)
    return () => window.removeEventListener('resize', checkScreenSize)
  }, [])

  // Auto-like pending track after sign-in
  useEffect(() => {
    if (pendingLikeTrack && user) {
      // User just signed in, auto-like the pending track
      const isLiked = trackList.likedTrackIds.has(pendingLikeTrack.id)
      if (!isLiked) {
        trackList.handleToggleLike(pendingLikeTrack)
      }
      setPendingLikeTrack(null)
    }
  }, [user, pendingLikeTrack])

  useEffect(() => {
    return () => {
      if (!isHmrUpdating) {
        const currentPath = window.location.pathname
        if (currentPath !== '/') {
          reset()
        }
      }
    }
  }, [reset])

  const loadMore = useCallback(async () => {
    await loadPublicTracks(true)
  }, [loadPublicTracks])

  return (
    <div className="w-[100dvw] h-[100dvh] overflow-hidden text-[#f0f0f0] flex flex-col">
      <NavBar
        onPlay={trackList.handleNavBarPlay}
        onStop={trackList.handleNavBarStop}
        rightSideButtons={
          <>
            <Link
              href="/generate"
              className="px-4 h-8 text-sm border-none rounded-md cursor-pointer font-semibold bg-gradient-to-br from-pink-500 to-fuchsia-700 text-white hover:opacity-90 flex items-center gap-2"
            >
              <SparkleIcon weight="regular" size={16} />
              <span className="hidden md:inline">AI Generate</span>
            </Link>
            <Link
              href="/tutorials"
              className="px-4 h-8 text-sm border-none rounded-md cursor-pointer font-semibold bg-gradient-to-br from-blue-500 to-blue-700 text-white hover:opacity-90 flex items-center gap-2"
            >
              <BookOpenTextIcon weight="regular" size={16} />
              <span className="hidden md:inline">Tutorials</span>
            </Link>
            {!isVerySmallScreen && (
              <Link
                href="/editor"
                className="px-4 h-8 text-sm border-none rounded-md cursor-pointer font-semibold bg-gradient-to-br from-orange-500 to-red-600 text-white hover:opacity-90 flex items-center gap-2"
              >
                <CodeIcon weight="regular" size={16} /> Switch to Editor
              </Link>
            )}
          </>
        }
      />

      <div className="relative" style={{ height: `calc(100dvh - ${navBarHeight}px)` }}>
        <div className="relative h-full overflow-y-auto overflow-x-hidden p-10">
          <PaginatedTrackList
            tracks={tracks}
            loading={loading}
            header={
              <span className="text-4xl font-extrabold font-[Turret_Road] text-lime-400">
                Latest loops:
              </span>
            }
            activeTrackId={trackList.activeTrackId}
            actuallyPlaying={trackList.actuallyPlaying}
            trackParents={trackList.trackParents}
            trackRemixes={trackList.trackRemixes}
            likedTrackIds={trackList.likedTrackIds}
            activeRemixId={trackList.activeRemixId}
            compileMode={trackList.compileMode}
            hasMore={hasMore}
            loadMore={loadMore}
            onSetActiveTrackId={trackList.setActiveTrackId}
            onSetActiveRemixId={trackList.setActiveRemixId}
            onSetActuallyPlaying={trackList.setActuallyPlaying}
            onLoad={trackList.handleLoad}
            onLoadInA={trackList.handleLoadInA}
            onLoadInB={trackList.handleLoadInB}
            onToggleLike={trackList.handleToggleLike}
            onPlayRemix={trackList.handlePlayRemix}
            onStopRemix={trackList.handleStopRemix}
            onOpenAuthModal={(track?: Track) => {
              if (track) {
                setPendingLikeTrack(track)
              }
              setShowAuthModal(true)
            }}
          />
        </div>
      </div>
      {showAuthModal && (
        <AuthModal
          onClose={() => {
            setShowAuthModal(false)
            setPendingLikeTrack(null)
          }}
          onSignInSuccess={() => {
            // The useEffect will handle the auto-like when user state updates
          }}
        />
      )}
      {showAboutModal && (
        <TermsAndPrivacyModal
          onClose={() => setShowAboutModal(false)}
          initialTab="about"
        />
      )}
      <button
        onClick={() => setShowAboutModal(true)}
        className="fixed bottom-4 right-4 z-10 text-sm text-[#888] hover:text-[#aaa] cursor-pointer"
      >
        About
      </button>
      <ToastContainer />
    </div>
  )
}
